import React, { useCallback, useEffect, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { faCirclePlay } from '@fortawesome/free-solid-svg-icons';
import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment-timezone';

import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';
import OrderStatusBadge from 'src/components/custom_components/OrderStatusBadge';
import { ResumeInformationModal } from 'src/components/ResumeInformationModal';

import { Api } from 'src/models/Api.model';
import { CriteriaGroupGroupElementsInnerOperatorEnum } from 'src/models/GalenData/CriteriaGroupGroupElementsInner.model';
import { RetinalExamKeyFiledEnum } from 'src/models/GalenData/CustomDeviceDataModel.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { SortOrderEnum } from 'src/models/GalenData/SortOrder.model';
import { I18NextNS } from 'src/models/Language.model';
import { ReportTemplateName } from 'src/models/ReportTemplate.model';
import { Session, transformExamToSession } from 'src/models/Session.model';

import useGetRetinalExamDataModelConfig from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useLoadSessions from 'src/hooks/useLoadSessions';

interface PatientInfoDrawerProps {
  show: boolean;
  onHide: () => void;
  formValues?: Partial<Session>;
  setShowConfirmModal: (show: boolean) => void;
  updateOrderId: (orderId: string) => void;
}

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default function PatientInfoDrawer({
  show,
  onHide,
  formValues,
  setShowConfirmModal,
  updateOrderId,
}: PatientInfoDrawerProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);
  const { t: tPages } = useTranslation(I18NextNS.PAGES);
  const history = useHistory();
  const { currentPage, handleSetCurrentPage } = useLoadSessions();

  const { deviceDataModelId } = useGetRetinalExamDataModelConfig();

  const [isLoading, setIsLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<Session>();

  const [isShowResumeModal, setIsShowResumeModal] = useState(false);
  const [ordersExams, setOrdersExams] = useState<
    Array<{
      order: DeviceDataView<OrderData>;
      session?: Session;
    }>
  >([]);

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const [pageSize, setPageSize] = useState(10);

  const fetchOrders = useCallback(async () => {
    try {
      if (!formValues?.patientUserId || !show) {
        return;
      }

      setIsLoading(true);

      const ordersPage = await Api.remote.get_exam_order({
        ownerFilter: {
          users: [formValues.patientUserId],
        },
        params: {
          sortBy: ['minValueProvidedOn'],
          sortOrder: SortOrderEnum.DESC,
        },
      });

      const examPromises = ordersPage.data.content?.map(async (orderData) => {
        const result = await Api.remote.get_exams({
          deviceDataModelId,
          ownerFilter: {
            users: [formValues.patientUserId ?? ''],
          },
          groupElements: [
            {
              key: RetinalExamKeyFiledEnum.OrderId,
              value: orderData.data.OrderId.value,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
            },
            {
              key: RetinalExamKeyFiledEnum.IsHidden,
              operator: CriteriaGroupGroupElementsInnerOperatorEnum.Equal,
              value: false,
            },
          ],
        });

        return {
          order: orderData,
          session: result.data.content?.[0]
            ? transformExamToSession(result.data.content?.[0])
            : undefined,
        };
      });

      const examResults = await Promise.all(examPromises || []);
      setOrdersExams(examResults);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    formValues?.patientUserId,
    show,
    deviceDataModelId,
    t,
    language,
    handleHttpRequestError,
  ]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  type TableDataType = {
    orderData: DeviceDataView<OrderData>;
    session?: Session;
    id: string;
  };

  const columns: Column<TableDataType>[] = [
    {
      title: t('orderFields.orderDate'),
      dataIndex: 'orderDate',
      render: (record: TableDataType) =>
        moment(record.orderData.data.OrderStatusDatetime.value)
          .tz(userTimeZone)
          .format('YYYY-MM-DD HH:mm z'),
    },
    {
      title: t('orderFields.orderType'),
      dataIndex: 'orderType',
      render: (record: TableDataType) =>
        t(
          `report.reportTemplateFields.${ReportTemplateName(record.orderData.data.OrderType.value)}`,
        ),
    },
    {
      title: t('orderFields.orderStatus'),
      dataIndex: 'status',
      render: (record: TableDataType) =>
        OrderStatusBadge(record.orderData.data.Status.value),
    },
    {
      title: t('orderFields.actions'),
      dataIndex: 'actions',
      render: (record: TableDataType) => {
        const orderData = record.orderData;
        const examData = record.session;
        const isInProgress = examData?.id !== undefined;

        return (
          <div className="d-flex">
            {orderData.data.Status.value === OrderStatusEnum.OPEN &&
              !isInProgress && (
                <Button
                  variant="outline-primary"
                  className="rounded-3 px-4 py-1 me-2"
                  onClick={() => {
                    setShowConfirmModal(true);
                    updateOrderId(orderData.data.OrderId.value);
                  }}
                  data-test="start-exam-button"
                >
                  <FontAwesomeIcon icon={faCirclePlay} />
                </Button>
              )}

            {orderData.data.Status.value === OrderStatusEnum.OPEN &&
              isInProgress && (
                <Button
                  variant="outline-primary"
                  className="rounded-3 px-4 py-1"
                  disabled={!isInProgress}
                  onClick={() => {
                    if (record.session) {
                      setCurrentSession({
                        ...record.session,
                        order: orderData.data,
                        template: orderData.data.OrderType.value,
                      });
                      setIsShowResumeModal(true);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faArrowRotateRight} />
                </Button>
              )}
          </div>
        );
      },
    },
  ];

  const tableData: TableDataType[] = ordersExams.map((item) => ({
    orderData: item.order,
    session: item.session,
    id: item.order.data.OrderId.value,
  }));

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = tableData.slice(startIndex, endIndex);

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          setPageSize(10);
          handleSetCurrentPage(1);
          onHide();
        }}
        placement="end"
        style={{
          width: '50%',
          maxWidth: '50vw',
        }}
      >
        <div className="d-flex flex-column h-100 p-3">
          <div className="d-flex justify-content-between align-items-center">
            <h5 data-sentry-mask>
              {formValues?.firstName} {formValues?.lastName}
            </h5>
            <Button variant="close" onClick={onHide} />
          </div>

          <div>
            <small className="text-muted">
              <span data-sentry-mask>
                {t('patientFields.DOB')}:{' '}
                {moment(formValues?.dob).format('YYYY-MM-DD')} |{' '}
              </span>

              <span data-sentry-mask>
                {t('patientFields.patientID')}: {formValues?.id}
              </span>
            </small>
          </div>

          <div className="flex-grow-1 overflow-auto">
            <CustomTable
              isLoading={isLoading}
              dataSource={paginatedData}
              columns={columns}
              rowKey="id"
              pagination={{
                defaultPageSize: 10,
                pageSizeOptions: [10, 20, 50],
                totalPages: Math.ceil(tableData.length / pageSize),
                currentPage: currentPage,
                handleSetCurrentPage: handleSetCurrentPage,
                onChangePage: (page: number, newPageSize: number) => {
                  handleSetCurrentPage(page);
                  setPageSize(newPageSize);
                },
              }}
              empty={{
                buttons: [],
              }}
            />
          </div>

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="primary-100"
              onClick={() => {
                onHide();
                history.push({
                  pathname: '/results',
                  state: { keyword: formValues?.id },
                });
              }}
            >
              {tPages('newExam.searchResults')}
            </Button>
          </div>
        </div>
      </Offcanvas>

      {currentSession && (
        <ResumeInformationModal
          currentSession={currentSession}
          isShowResumeModal={isShowResumeModal}
          handleSetIsShowResumeModal={setIsShowResumeModal}
        />
      )}
    </>
  );
}
